import { observable, runInAction } from 'mobx';
import ErrorUtil from '../error/ErrorUtil';
import API from '../util/ApiUtil';
import LanguageStore from './LanguageStore';
import { RootStore } from './RootStore';

const THIRTY_SECONDS = 30 * 1000;

export interface TvChannelMetadata {
  label: string;
  value: string;
}

export interface TvChannel {
  title: string;
  description?: string;
  image: string;
  meta: TvChannelMetadata[];
  icons?: { [key: string]: string };
}

export interface TvPacket {
  title: string;
  description?: string;
  channels: TvChannel[];
}

export interface BusinessTvPacket extends TvPacket {
  price: number;
  productOfferingCode: string;
  liableOnlyForSalesConsultant: boolean;
}

export interface BusinessTvPacketHolder {
  tvPackets?: BusinessTvPacket[];
  status: string;
}

export interface TvPacketHolder {
  tvPacket?: TvPacket;
  status: string;
}

export interface TvChannelsHolder {
  channels?: TvChannel[];
  status: string;
}

class TvChannelsStore {
  @observable accessor businessTvPackets: BusinessTvPacketHolder = {
    status: 'UNINITIALIZED',
  };
  @observable accessor isTvB2BPacketDetailsModalOpen: boolean = false;
  @observable accessor chosenPacket: string = '';
  @observable accessor tvPacketMap: Map<string, TvPacketHolder> = new Map();
  @observable accessor tvRecordableChannelsMap: Map<string, TvChannelsHolder> = new Map();

  private languageStore: LanguageStore;

  constructor(rootStore: RootStore) {
    this.languageStore = rootStore.languageStore;
  }

  public getTvPacket(packetGroupId: number, packetId: number, lang: string = this.languageStore.currentLanguage) {
    return this.getTvPacketHolder(packetGroupId, packetId, lang).tvPacket;
  }

  public getRecordableChannels(packetGroupId: number, lang: string = this.languageStore.currentLanguage) {
    return this.getTvRecordableChannelHolder(packetGroupId, lang);
  }

  public loadBusinessTvPacket = async () => {
    try {
      runInAction(() => {
        this.businessTvPackets.status = 'FETCHING';
      });
      const response = await API.get(`/tv-comparison/business/${this.languageStore.currentLanguage}`);
      runInAction(() => {
        this.businessTvPackets.tvPackets = response.data.tvPackages;
        this.businessTvPackets.status = 'FETCHED';
      });
    } catch (e) {
      runInAction(() => {
        this.businessTvPackets.status = 'ERROR';
        ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      });
    }
  };

  public async loadTvPacket(packetGroupId: number, packetId: number, lang: string = this.languageStore.currentLanguage) {
    const key = this.getKey(packetGroupId, lang, packetId);
    if (this.packetFetchNotRequired(key)) {
      return Promise.resolve();
    }
    runInAction(() => {
      this.tvPacketMap.set(key, { status: 'FETCHING' });
    });
    return this.fetchTvPacket(lang, packetGroupId, packetId);
  }

  public async loadRecordableChannels(packetGroupId: number, lang: string = this.languageStore.currentLanguage) {
    const key = this.getKey(packetGroupId, lang);
    if (this.recordableChannelsFetchNotRequired(key)) {
      return Promise.resolve();
    }
    runInAction(() => {
      this.tvRecordableChannelsMap.set(key, { status: 'FETCHING' });
    });
    return this.fetchRecordableTvChannels(lang, packetGroupId);
  }

  public getTvPacketMapStatus(key: string) {
    return this.tvPacketMap.get(key).status;
  }

  public getTvPacketMapSize() {
    return this.tvPacketMap.size;
  }

  public notFound(packetGroupId: number, packetId?: number, lang: string = this.languageStore.currentLanguage) {
    return packetId
      ? this.getTvPacketHolder(packetGroupId, packetId, lang).status === 'NOT_FOUND'
      : this.getTvRecordableChannelHolder(packetGroupId, lang).status === 'NOT_FOUND';
  }

  public isFetching(packetGroupId: number, packetId?: number, lang: string = this.languageStore.currentLanguage) {
    return packetId
      ? ['UNINITIALIZED', 'FETCHING'].includes(this.getTvPacketHolder(packetGroupId, packetId, lang).status)
      : ['UNINITIALIZED', 'FETCHING'].includes(this.getTvRecordableChannelHolder(packetGroupId, lang).status);
  }

  private async fetchRecordableTvChannels(lang: string, packetGroupId: number) {
    try {
      const response = await API.get(`/tv-channels/${lang}/recordable-channels/${packetGroupId}`, { timeout: THIRTY_SECONDS });
      runInAction(() => {
        this.tvRecordableChannelsMap.set(this.getKey(packetGroupId, lang), {
          status: 'FETCHED',
          channels: response.data,
        });
      });
    } catch (e) {
      runInAction(() => {
        this.tvRecordableChannelsMap.set(this.getKey(packetGroupId, lang), {
          status: 'NOT_FOUND',
        });
      });
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
    }
  }

  private async fetchTvPacket(lang: string, packetGroupId: number, packetId: number) {
    try {
      const response = await API.get(`/tv-channels/${lang}/packet/${packetGroupId}/${packetId}`, { timeout: THIRTY_SECONDS });
      runInAction(() => {
        this.tvPacketMap.set(this.getKey(packetGroupId, lang, packetId), {
          status: 'FETCHED',
          tvPacket: response.data,
        });
      });
    } catch (e) {
      runInAction(() => {
        this.tvPacketMap.set(this.getKey(packetGroupId, lang, packetId), {
          status: 'NOT_FOUND',
        });
      });
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
    }
  }

  private getTvPacketHolder(packetGroupId: number, packetId: number, lang: string = this.languageStore.currentLanguage) {
    return (
      this.tvPacketMap.get(this.getKey(packetGroupId, lang, packetId)) || {
        status: 'UNINITIALIZED',
      }
    );
  }

  private getTvRecordableChannelHolder(packetGroupId: number, lang: string = this.languageStore.currentLanguage) {
    return (
      this.tvRecordableChannelsMap.get(this.getKey(packetGroupId, lang)) || {
        status: 'UNINITIALIZED',
      }
    );
  }

  private getKey(packetGroupId: number, lang: string, packetId?: number) {
    return `${lang}_${packetGroupId}${packetId ? `_${packetId}` : ''}`;
  }

  private packetFetchNotRequired(key: string) {
    const packet = this.tvPacketMap.get(key);
    return packet && packet.status !== 'NOT_FOUND';
  }

  private recordableChannelsFetchNotRequired(key: string) {
    const recordableChannels = this.tvRecordableChannelsMap.get(key);
    return recordableChannels && recordableChannels.status !== 'NOT_FOUND';
  }
}

export default TvChannelsStore;
